import { useState, useEffect } from 'react';

/**
 * Helper function to determine device type from width
 */
const getDeviceType = (width) => {
  if (width < 768) {
    return 'mobile';
  } else if (width < 1024) {
    return 'tablet';
  } else {
    return 'desktop';
  }
};

/**
 * Hook to detect device type based on screen width
 * Returns 'mobile' for screens < 768px, 'tablet' for 768-1023px, 'desktop' for ≥1024px
 */
export const useDeviceType = () => {
  // Initialize based on actual window width (not just 'desktop')
  const [deviceType, setDeviceType] = useState(() => {
    if (typeof window !== 'undefined') {
      return getDeviceType(window.innerWidth);
    }
    return 'desktop'; // SSR fallback
  });

  useEffect(() => {
    const checkDeviceType = () => {
      const width = window.innerWidth;
      const newDeviceType = getDeviceType(width);
      setDeviceType(newDeviceType);
    };

    // Check on mount (in case initial state was wrong)
    checkDeviceType();

    // Add resize listener
    window.addEventListener('resize', checkDeviceType);

    // Cleanup
    return () => window.removeEventListener('resize', checkDeviceType);
  }, []);

  return deviceType;
};

/**
 * Hook to check if device is mobile (< 768px)
 */
export const useIsMobile = () => {
  const deviceType = useDeviceType();
  return deviceType === 'mobile';
};

/**
 * Hook to check if device is tablet (768px - 1023px)
 */
export const useIsTablet = () => {
  const deviceType = useDeviceType();
  return deviceType === 'tablet';
};

/**
 * Hook to check if device is desktop (≥ 1024px)
 */
export const useIsDesktop = () => {
  const deviceType = useDeviceType();
  return deviceType === 'desktop';
};

/**
 * Hook to check if device is mobile or tablet (for responsive design)
 */
export const useIsMobileOrTablet = () => {
  const deviceType = useDeviceType();
  return deviceType === 'mobile' || deviceType === 'tablet';
};
